import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../bom/ui/Toast';
import { sbomApi, itemUrl } from './sbomApi';
import useStickyHead from './useStickyHead';
import SbomExportModal from './SbomExportModal';
import SbomCompareModal from './SbomCompareModal';
import SbomWhereUsedModal from './SbomWhereUsedModal';
import SbomDeleteModal from './SbomDeleteModal';
import { WhereUsedIcon } from '../bom/tree/icons';
import { AssessDrawer, VulnChips, WorstVerdict } from './SbomAssess';
import { ScanLine, useScan } from './SbomVulns';

const SVG = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round',
  strokeLinejoin: 'round', 'aria-hidden': true, focusable: 'false' };

function ChevronIcon({ up, size = 20 }) {
  return <svg {...SVG} width={size} height={size} strokeWidth={2.4}><path d={up ? 'M6 15l6-6 6 6' : 'M6 9l6 6 6-6'} /></svg>;
}
function DotsIcon({ size = 20 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true" focusable="false">
      <circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" />
    </svg>
  );
}
function ImportIcon({ size = 16 }) {
  return <svg {...SVG} width={size} height={size}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" /></svg>;
}
function CompareIcon({ size = 16 }) {
  return <svg {...SVG} width={size} height={size}><path d="M16 3h5v5M4 20 21 3M21 16v5h-5M15 15l6 6M4 4l5 5" /></svg>;
}
function TraceIcon({ size = 16 }) {
  return <svg {...SVG} width={size} height={size}><path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" /></svg>;
}
function TrashIcon({ size = 16 }) {
  return (
    <svg {...SVG} width={size} height={size}>
      <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6" />
    </svg>
  );
}

/** The ⋮ menu on the right of the page head: Import, Compare, Delete. Closes on outside click / Escape. */
function MoreMenu({ items }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return (
    <div className="sbom-more" ref={ref}>
      <button type="button" className="sbom-ibtn sbom-ibtn--lg" aria-label="More actions" aria-haspopup="menu"
        aria-expanded={open} title="More actions" onClick={() => setOpen((o) => !o)}><DotsIcon /></button>
      {open && (
        <div className="sbom-more-menu" role="menu">
          {items.map((it) => (
            <button key={it.label} type="button" role="menuitem" disabled={it.disabled} title={it.title}
              className={`sbom-more-item${it.danger ? ' sbom-more-item--danger' : ''}`}
              onClick={() => { setOpen(false); it.onClick(); }}>
              <span className="sbom-more-ic">{it.icon}</span>{it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function OpenIcon({ size = 14 }) {
  return (
    <svg viewBox="0 0 16 16" width={size} height={size} aria-hidden="true" focusable="false">
      <rect x="2" y="2" width="12" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M5 5.5h6M5 8h6M5 10.5h3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}

function CopyIcon({ size = 14 }) {
  return (
    <svg viewBox="0 0 16 16" width={size} height={size} aria-hidden="true" focusable="false">
      <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M10.5 3.5v-.5A1.5 1.5 0 0 0 9 1.5H4A1.5 1.5 0 0 0 2.5 3v5A1.5 1.5 0 0 0 4 9.5h.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}

const MAX_EXPAND_DEPTH = 20;
const COMPACT_KEY = 'sbom.releases.compact';
const MAX_ROWS = 1000;   // rows drawn at once — a shared package repeats under every parent

/**
 * Software releases — one card per release, then the tree.
 *
 * The tree is read one level at a time (`/children`) and cached per (release, parent). A row's
 * identity is its PATH, because one package sits under several parents and each occurrence
 * expands on its own — the same rule as the hardware BOM.
 */
export default function SbomReleases({ info, onAuth }) {
  const headBox = useStickyHead();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const s = info.settings;
  const [releases, setReleases] = useState(null);
  const [warning, setWarning] = useState('');
  const [error, setError] = useState('');
  // `?release=<item id>` — the SBOM link field on an Orcanos release opens it here.
  const [params, setParams] = useSearchParams();
  const linked = params.get('release') || '';
  const [sel, setSel] = useState(linked);
  const [kids, setKids] = useState({});          // `${rel}|${parent}` → rows | {loading} | {error}
  const [open, setOpen] = useState(() => new Set());
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [exportFor, setExportFor] = useState(null);
  const [compare, setCompare] = useState(false);
  const [cmpPair, setCmpPair] = useState(null);    // the compare's last From / To, reopened by "Back to compare"
  const [locate, setLocate] = useState(null);      // {rel, ids: Set, label, pending, places} — from Compare → Locate
  const locScrolled = useRef(false);
  const [whereFor, setWhereFor] = useState('');
  const [liveError, setLiveError] = useState('');
  const [deleteFor, setDeleteFor] = useState(null);
  const [tracing, setTracing] = useState(false);
  // Component → Release traces for a release imported before the import made them (2026-10-23).
  // Runs on the server; polled here until it ends, then one toast with the outcome.
  const traceRelease = async (rel) => {
    setTracing(true);
    try {
      const st = await sbomApi.traceRelease(rel.item_id);
      showToast(`Tracing ${st.components} components to ${rel.name}…`, 'info');
      let j = st;
      for (let i = 0; i < 1200 && j.running; i += 1) {
        await new Promise((ok) => { setTimeout(ok, 1500); });
        j = await sbomApi.traceStatus(rel.item_id);
      }
      const msg = `${rel.name}: ${j.traced || 0} traced${j.already ? `, ${j.already} already traced` : ''}`
        + `${j.failed ? `, ${j.failed} refused — ${(j.errors || [])[0] || ''}` : ''}.`;
      showToast(msg, j.failed ? 'error' : 'success');
    } catch (e) {
      if (!onAuth(e)) showToast(e.message, 'error');
    } finally {
      setTracing(false);
    }
  };
  const [deleted, setDeleted] = useState(null);   // the release just removed — still in Orcanos
  const [assess, setAssess] = useState(null);     // {id, package_key} — the Assess drawer
  const vulnOn = s.osvEnabled !== false;
  const [compact, setCompact] = useState(() => {   // release cards folded to a one-line scorecard
    try { return localStorage.getItem(COMPACT_KEY) === '1'; } catch { return false; }
  });
  const toggleCompact = () => setCompact((c) => {
    try { localStorage.setItem(COMPACT_KEY, c ? '0' : '1'); } catch { /* private window */ }
    return !c;
  });

  const load = useCallback(() => {
    setError('');
    sbomApi.releases()
      .then((d) => {
        setReleases(d.releases || []);
        setWarning(d.warning || '');
        if (d.scan) setScanRef.current?.(d.scan);
        setSel((cur) => (cur && (d.releases || []).some((r) => r.item_id === cur) ? cur : '')
          || (d.releases || []).find((r) => r.imported)?.item_id || '');
      })
      .catch((e) => { if (!onAuth(e)) setError(e.message); });
  }, [onAuth]);
  useEffect(() => { load(); }, [load]);
  // A finished check (or a saved verdict) changes the chips: read the release and its open levels again.
  const refresh = useCallback(() => { setKids({}); setTreeLoaded(new Set()); load(); }, [load]);   // eslint-disable-line no-use-before-define
  const [scan, startScan, setScan] = useScan(onAuth, refresh);
  const setScanRef = useRef(setScan);
  setScanRef.current = setScan;

  const fetchKids = useCallback(async (rel, parent, live = true) => {
    const k = `${rel}|${parent}`;
    setKids((m) => ({ ...m, [k]: { loading: true } }));
    try {
      const d = await sbomApi.children(rel, parent, live);
      if (d.live_error) setLiveError(d.live_error);
      setKids((m) => ({ ...m, [k]: d.rows || [] }));
      return d.rows || [];
    } catch (e) {
      if (!onAuth(e)) setKids((m) => ({ ...m, [k]: { error: e.message } }));
      return [];
    }
  }, [onAuth]);

  const toggle = (pathId, rel, parent) => {
    const opening = !open.has(pathId);
    setOpen((cur) => {
      const next = new Set(cur);
      if (opening) next.add(pathId); else next.delete(pathId);
      return next;
    });
    if (opening && !kids[`${rel}|${parent}`]) fetchKids(rel, parent);
  };

  const selected = releases?.find((r) => r.item_id === sel);
  // The link has done its job once the release is open — drop it so a reload doesn't pin it.
  useEffect(() => { if (linked && releases) setParams({}, { replace: true }); }, [linked, releases, setParams]);

  useEffect(() => {
    if (sel && !kids[`${sel}|`] && selected?.imported) {
      setOpen((cur) => new Set(cur).add(sel));
      fetchKids(sel, '');
    }
  }, [sel, selected, kids, fetchKids]);

  // Reads every level of the release that is not cached yet (no live overlay — one call per
  // DISTINCT parent, one state update at the end). Walking by path instead froze the page: a
  // package shared by many parents is reached by exponentially many paths in a deep tree.
  const loadTree = useCallback(async (rel) => {
    const cache = { ...kids };
    const added = {};
    const done = new Set();
    let level = [''];
    for (let depth = 0; level.length && depth <= MAX_EXPAND_DEPTH; depth += 1) {
      const next = [];
      for (const parent of level) {
        if (done.has(parent)) continue;
        done.add(parent);
        const k = `${rel}|${parent}`;
        let list = Array.isArray(cache[k]) ? cache[k] : null;
        if (!list) {
          try {
            const d = await sbomApi.children(rel, parent, false);
            list = d.rows || [];
          } catch (e) {
            if (onAuth(e)) return cache;
            list = [];
          }
          cache[k] = list;
          added[k] = list;
        }
        for (const r of list) if (r.has_children && !done.has(r.item_id)) next.push(r.item_id);
      }
      level = next;
    }
    if (Object.keys(added).length) setKids((m) => ({ ...m, ...added }));
    return cache;
  }, [kids, onAuth]);

  const expandAll = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      const cache = await loadTree(sel);
      // Open paths until MAX_ROWS rows are drawn — a shared package repeats under every parent.
      const ids = [sel];
      let drawn = 0;
      const walk = (parent, path, depth, seen) => {
        for (const r of cache[`${sel}|${parent}`] || []) {
          if (drawn >= MAX_ROWS) return;
          drawn += 1;
          if (!r.has_children || depth >= MAX_EXPAND_DEPTH || seen.has(r.item_id)) continue;
          const pid = `${path}/${r.item_id}`;
          ids.push(pid);
          walk(r.item_id, pid, depth + 1, new Set(seen).add(r.item_id));
        }
      };
      walk('', sel, 1, new Set());
      setOpen((cur) => new Set([...cur, ...ids]));
      if (drawn >= MAX_ROWS) showToast(`Expanded the first ${MAX_ROWS} rows. Search to find a package.`, 'info');
    } finally { setBusy(false); }
  };

  // Search covers the WHOLE release, not only the expanded rows: the first keystroke reads the
  // rest of the tree once, then matches are shown with the path that leads to them.
  const needle = q.trim().toLowerCase();
  const [treeLoaded, setTreeLoaded] = useState(() => new Set());
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    if (!needle || !sel || !selected?.imported || treeLoaded.has(sel) || searching) return;
    setSearching(true);
    loadTree(sel)
      .then(() => setTreeLoaded((cur) => new Set(cur).add(sel)))
      .finally(() => setSearching(false));
  }, [needle, sel, selected, treeLoaded, searching, loadTree]);

  // Compare → Locate: select the release, read its tree once, then open the path to EVERY place the
  // package's item(s) sit (a shared package appears under each parent) and highlight those rows.
  const startLocate = (rel, ids, label, pair) => {
    setCmpPair(pair);
    setCompare(false);
    setQ('');
    setSel(rel);
    locScrolled.current = false;
    setLocate({ rel, ids: new Set(ids.map(String)), label, pending: true, places: 0 });
  };
  // Clicking a highlighted row clears its highlight; the last one ends the Locate.
  const unlocate = (pathId) => setLocate((l) => {
    if (!l) return l;
    const cleared = new Set(l.cleared || []).add(pathId);
    return cleared.size >= l.places ? null : { ...l, cleared };
  });
  const locPending = locate?.pending ? locate : null;
  useEffect(() => {
    if (!locPending) return;
    let off = false;
    loadTree(locPending.rel).then((cache) => {
      if (off) return;
      const rel = locPending.rel;
      const openIds = new Set([rel]);
      let places = 0;
      let visits = 0;
      const empty = new Set();   // parents with nothing located below — same answer on every path
      const walk = (parent, path, seen) => {
        if (empty.has(parent)) return false;
        let any = false;
        for (const r of cache[`${rel}|${parent}`] || []) {
          if (places >= MAX_ROWS || (visits += 1) > 50000) return any;
          const pid = `${path}/${r.item_id}`;
          if (locPending.ids.has(String(r.item_id))) { places += 1; any = true; }
          if (r.has_children && !seen.has(r.item_id) && walk(r.item_id, pid, new Set(seen).add(r.item_id))) {
            openIds.add(pid);
            any = true;
          }
        }
        if (!any) empty.add(parent);
        return any;
      };
      walk('', rel, new Set());
      setTreeLoaded((cur) => new Set(cur).add(rel));
      setOpen(openIds);
      setLocate((l) => (l && l.rel === rel ? { ...l, pending: false, places } : l));
    });
    return () => { off = true; };
  }, [locPending]);   // eslint-disable-line react-hooks/exhaustive-deps -- runs once per Locate; loadTree changes with every fetched level

  // Matches are counted per PACKAGE (distinct item), not per path it appears on.
  const found = useMemo(() => {
    if (!needle || !selected) return 0;
    const prefix = `${selected.item_id}|`;
    const hit = new Set();
    for (const [k, list] of Object.entries(kids)) {
      if (!k.startsWith(prefix) || !Array.isArray(list)) continue;
      for (const r of list) {
        if (`${r.name} ${r.version} ${r.key} ${r.supplier || ''} ${r.purl || ''}`.toLowerCase().includes(needle)) hit.add(r.item_id);
      }
    }
    return hit.size;
  }, [needle, selected, kids]);

  const rows = useMemo(() => {
    if (!selected) return [];
    const out = [];
    const rel = selected.item_id;
    const isMatch = (r) => `${r.name} ${r.version} ${r.key} ${r.supplier || ''} ${r.purl || ''}`.toLowerCase().includes(needle);
    const memo = new Map();
    const below = (parent, seen) => {          // does anything under `parent` match?
      if (memo.has(parent)) return memo.get(parent);
      const list = kids[`${rel}|${parent}`];
      const hit = Array.isArray(list) && list.some((r) => isMatch(r)
        || (r.has_children && !seen.has(r.item_id) && below(r.item_id, new Set(seen).add(r.item_id))));
      memo.set(parent, hit);
      return hit;
    };
    const walk = (parent, path, depth, seen) => {
      const list = kids[`${rel}|${parent}`];
      if (!Array.isArray(list)) {
        if (list?.loading) out.push({ kind: 'loading', id: `${path}/…`, depth });
        if (list?.error) out.push({ kind: 'error', id: `${path}/!`, depth, error: list.error });
        return;
      }
      for (const r of list) {
        if (out.length >= MAX_ROWS) return;
        const pid = `${path}/${r.item_id}`;
        const canOpen = r.has_children && !seen.has(r.item_id);
        const nextSeen = new Set(seen).add(r.item_id);
        if (!needle) {
          const isOpen = open.has(pid);
          out.push({ kind: 'comp', id: pid, depth, r, isOpen });
          if (isOpen && canOpen) walk(r.item_id, pid, depth + 1, nextSeen);
          continue;
        }
        const match = isMatch(r);
        const leadsTo = canOpen && below(r.item_id, nextSeen);
        if (!match && !leadsTo) continue;
        out.push({ kind: 'comp', id: pid, depth, r, isOpen: leadsTo, dim: !match });
        if (leadsTo) walk(r.item_id, pid, depth + 1, nextSeen);
      }
    };
    if (needle || open.has(rel)) walk('', rel, 1, new Set());
    return out;
  }, [selected, kids, open, needle]);

  // A located package is brought into view once, after its paths are drawn (Note #113: the page
  // scrolls inside .app-scroll, so scrollIntoView — never window.scrollTo).
  useEffect(() => {
    if (!locate || locate.pending || locScrolled.current) return;
    locScrolled.current = true;
    requestAnimationFrame(() => {
      document.querySelector('.sbom-row--located')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  }, [locate, rows]);

  // Typing a search or picking another release ends the Locate.
  useEffect(() => { if (needle) setLocate(null); }, [needle]);
  useEffect(() => { setLocate((l) => (l && l.rel !== sel ? null : l)); }, [sel]);

  if (error) return <div className="sbom-error" role="alert">{error}</div>;
  if (!releases) return <div className="sbom-muted">Loading releases…</div>;

  const relLink = (r) => itemUrl(info.baseUrl, s.versionId, s.releaseType, r.item_id);
  const cols = vulnOn ? 9 : 7;
  const compLink = (r) => itemUrl(info.baseUrl, s.versionId, s.componentType, r.item_id);

  return (
    <>
      <div className="sbom-head">
        <div>
          <div className="sbom-head-t">
            <h2>Software releases</h2>
            {releases.length > 0 && (
              <button type="button" className="sbom-ibtn sbom-ibtn--lg sbom-fold" onClick={toggleCompact} aria-expanded={!compact}
                aria-label={compact ? 'Expand the release cards' : 'Minimize the release cards'}
                title={compact ? 'Show the full release cards' : 'Fold the release cards to a small scorecard'}>
                <ChevronIcon up={!compact} />
              </button>
            )}
          </div>
          {!compact && <div className="sbom-sub">Each release is the SBOM of one software version. Expand it to see what it
            contains, direct and indirect.</div>}
        </div>
        <div className="sbom-sp" />
        {/* The check covers every release of the account, so it sits on the page, not on one release's grid. */}
        {vulnOn && <span className="sbom-scanline" title="Checks the components of all software releases">
          <ScanLine scan={scan} onCheck={() => startScan().catch((e) => showToast(e.message, 'error'))} />
        </span>}
        <MoreMenu items={[
          { label: 'Import SBOM', icon: <ImportIcon />, onClick: () => navigate('/bom/sw-import') },
          { label: 'Compare releases', icon: <CompareIcon />, onClick: () => setCompare(true),
            disabled: releases.filter((r) => r.imported).length < 2 && !releases.some((r) => r.baselines?.length),
            title: 'Needs two imported releases, or a kept baseline' },
          { label: 'Trace components in Orcanos', icon: <TraceIcon />, disabled: !selected?.imported || tracing,
            onClick: () => traceRelease(selected),
            title: selected?.imported ? 'Trace every component of this release to it in Orcanos (those already traced are skipped)'
              : 'Select an imported release first' },
          { label: selected?.imported ? `Delete ${selected.name || 'release'}…` : 'Delete release…', icon: <TrashIcon />,
            danger: true, disabled: !selected?.imported, onClick: () => setDeleteFor(selected),
            title: selected?.imported ? undefined : 'Select an imported release first' },
        ]} />
      </div>
      {deleted && (
        <div className="sbom-error" role="alert" style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <span style={{ flex: 1 }}>
            <b>{deleted.name}</b> was deleted from the Software BOM, but <b>not in Orcanos</b>. The release
            {deleted.key && <> (<a href={relLink(deleted)} target="_blank" rel="noreferrer">{deleted.key}</a>)</>} and its
            components are still there. Go to Orcanos to delete them. To import its SBOM again instead, import the file
            into that same release (Import SBOM → <i>An existing release</i>), so Orcanos does not get a second one.
          </span>
          <button type="button" className="sbom-modal-x" aria-label="Dismiss" onClick={() => setDeleted(null)}>×</button>
        </div>
      )}
      {warning && <div className="sbom-warn" style={{ marginBottom: 12 }}>{warning}</div>}
      {liveError && (
        <div className="sbom-warn" style={{ marginBottom: 12 }}>
          The Component filter could not be read ({liveError}). Values show as imported.
        </div>
      )}

      {releases.length === 0 ? (
        <div className="sbom-state">
          <h3>No software releases yet</h3>
          <p>Import an SBOM file from your build, or the spreadsheet you keep today. The import creates the
            release and its components in Orcanos.</p>
          <Link to="/bom/sw-import" className="sbom-btn sbom-btn--pri">Import SBOM</Link>
        </div>
      ) : compact ? (
        <div className="sbom-mini-cards">
          {releases.map((r) => (
            <button type="button" key={r.item_id} className={`sbom-mini${sel === r.item_id ? ' sbom-mini--sel' : ''}`}
              onClick={() => setSel(r.item_id)} title={r.key || undefined}>
              <b>{r.name || `Release ${r.item_id}`}</b>
              {r.imported ? (
                <span className="sbom-mini-n">
                  <span><b>{r.components}</b> comp</span>
                  <span><b>{r.direct}</b> direct</span>
                  <span><b>{r.components - r.direct}</b> indirect</span>
                  {vulnOn && r.vulns && <span className={r.vulns.open ? 'sbom-badtext' : undefined}><b>{r.vulns.open}</b> open</span>}
                </span>
              ) : (
                <span className="sbom-muted">no SBOM</span>
              )}
            </button>
          ))}
        </div>
      ) : (
        <div className="sbom-cards">
          {releases.map((r) => (
            <button type="button" key={r.item_id} className={`sbom-card${sel === r.item_id ? ' sbom-card--sel' : ''}`}
              onClick={() => setSel(r.item_id)}>
              <div className="sbom-card-t">
                <b>{r.name || `Release ${r.item_id}`}</b>
                {r.key && <span className="sbom-mono sbom-muted">{r.key}</span>}
                {r.status && <span className="sbom-pill sbom-pill--plain">{r.status}</span>}
              </div>
              {r.imported ? (
                <>
                  <div className="sbom-stats">
                    <div className="sbom-stat"><div className="sbom-stat-n">{r.components}</div><div className="sbom-stat-l">Components</div></div>
                    <div className="sbom-stat"><div className="sbom-stat-n">{r.direct}</div><div className="sbom-stat-l">Direct</div></div>
                    <div className="sbom-stat"><div className="sbom-stat-n">{r.components - r.direct}</div><div className="sbom-stat-l">Indirect</div></div>
                  </div>
                  {vulnOn && r.vulns && <VulnStats v={r.vulns} />}
                  <div className="sbom-tiny sbom-muted">
                    From <span className="sbom-mono">{r.file_name}</span> ({r.format}), {String(r.imported_at || '').slice(0, 10)} UTC
                    {!r.has_dependencies && ' · flat list, no dependency tree in the file'}
                    {r.baselines?.length > 0 && ` · ${r.baselines.length} earlier SBOM${r.baselines.length === 1 ? '' : 's'} kept as baseline${r.baselines.length === 1 ? '' : 's'}`}
                  </div>
                </>
              ) : (
                <div className="sbom-tiny sbom-muted">No SBOM imported yet.</div>
              )}
            </button>
          ))}
        </div>
      )}

      {selected && !selected.imported && (
        <div className="sbom-state">
          <h3>{selected.name} has no SBOM yet</h3>
          <p>Import its SBOM file and choose this release as the target.</p>
          <button type="button" className="sbom-btn sbom-btn--pri" onClick={() => navigate('/bom/sw-import')}>Import SBOM</button>
        </div>
      )}

      {selected?.imported && (
        <div className="sbom-table-card">
          <div className="sbom-tbar">
            <input type="search" className="sbom-search" placeholder="Find a package, e.g. numpy" value={q}
              onChange={(e) => setQ(e.target.value)} aria-label="Search packages" />
            {needle && (
              <span className="sbom-tiny sbom-muted">
                {searching ? 'Searching the whole release…'
                  : `${found} found in the whole release${rows.length >= MAX_ROWS ? ` · showing the first ${MAX_ROWS} rows` : ''}`}
              </span>
            )}
            {locate && locate.rel === selected.item_id && (
              <span className="sbom-locbar" role="status">
                {locate.pending ? <>Locating <b>{locate.label}</b>…</>
                  : locate.places ? <><b>{locate.label}</b> — {locate.places} place{locate.places === 1 ? '' : 's'} in this release</>
                    : <><b>{locate.label}</b> was not found in this release's tree</>}
                <button type="button" className="sbom-act" onClick={() => setCompare(true)}>↩ Back to compare</button>
                <button type="button" className="sbom-act" onClick={() => setLocate(null)}>Clear</button>
              </span>
            )}
            <span style={{ flex: 1 }} />
            <button type="button" className="sbom-btn sbom-btn--pri" onClick={() => setExportFor(selected)}>⤓ Export…</button>
          </div>
          <div className="sbom-tw sbom-tw--sticky" ref={headBox}>
            <table className="sbom-table">
              <thead>
                <tr>
                  <th>Component</th><th>Version</th><th className="sbom-hide-s">Type</th><th className="sbom-hide-s">Licence</th>
                  <th className="sbom-hide-s">Supplier</th>
                  {vulnOn && <><th>Vulnerabilities</th><th>Assessment</th></>}
                  <th className="sbom-hide-s">Support</th><th />
                </tr>
              </thead>
              <tbody>
                <tr className="sbom-root">
                  <td>
                    <div className="sbom-tree">
                      <button type="button" className="sbom-chev" aria-label={open.has(selected.item_id) ? 'Collapse' : 'Expand'}
                        onClick={() => toggle(selected.item_id, selected.item_id, '')}>{open.has(selected.item_id) ? '▾' : '▸'}</button>
                      <span className="sbom-glyph sbom-glyph--rel">R</span>
                      {selected.name}
                      {selected.key && <a className="sbom-key" href={relLink(selected)} target="_blank" rel="noreferrer">{selected.key}</a>}
                      {open.size > 1 ? (
                        <button type="button" className="sbom-ibtn sbom-ibtn--light sbom-ibtn--sm" onClick={() => setOpen(new Set([selected.item_id]))}
                          disabled={!!needle} aria-label="Collapse all" title="Collapse all"><ChevronIcon up size={16} /></button>
                      ) : (
                        <button type="button" className="sbom-ibtn sbom-ibtn--light sbom-ibtn--sm" onClick={expandAll} disabled={busy || !!needle}
                          aria-label="Expand all" title={busy ? 'Expanding…' : 'Expand all'}><ChevronIcon size={16} /></button>
                      )}
                    </div>
                  </td>
                  <td className="sbom-mono">{selected.version}</td>
                  <td className="sbom-hide-s">application</td><td className="sbom-hide-s" /><td className="sbom-hide-s" />
                  {vulnOn && <><td /><td /></>}<td className="sbom-hide-s" />
                  <td />
                </tr>
                {rows.map((row) => {
                  const rails = Array.from({ length: row.depth }, (_, i) => <span key={i} className="sbom-rail" />);
                  if (row.kind === 'loading') {
                    return <tr key={row.id}><td colSpan={cols}><div className="sbom-tree">{rails}<span className="sbom-muted sbom-tiny">Loading…</span></div></td></tr>;
                  }
                  if (row.kind === 'error') {
                    return <tr key={row.id}><td colSpan={cols}><div className="sbom-tree">{rails}<span className="sbom-error sbom-tiny">{row.error}</span></div></td></tr>;
                  }
                  const r = row.r;
                  const located = locate && !locate.pending && locate.ids.has(String(r.item_id)) && !locate.cleared?.has(row.id);
                  return (
                    <tr key={row.id} style={row.dim ? { opacity: 0.55 } : undefined}
                      className={located ? 'sbom-row--located' : undefined}
                      onClick={located ? () => unlocate(row.id) : undefined}>
                      <td>
                        <div className="sbom-tree">
                          {rails}
                          {r.has_children ? (
                            <button type="button" className="sbom-chev" aria-label={row.isOpen ? 'Collapse' : 'Expand'}
                              onClick={() => toggle(row.id, selected.item_id, r.item_id)}>{row.isOpen ? '▾' : '▸'}</button>
                          ) : <span className="sbom-chev sbom-chev--blank" aria-hidden="true" />}
                          <span className={`sbom-glyph ${r.direct ? 'sbom-glyph--dir' : 'sbom-glyph--ind'}`}
                            title={r.direct ? 'Direct dependency' : 'Brought in by another package'}>{r.direct ? 'D' : 'I'}</span>
                          {r.name}
                          {r.key && <a className="sbom-key" href={compLink(r)} target="_blank" rel="noreferrer">{r.key}</a>}
                        </div>
                      </td>
                      <td className="sbom-mono">{r.version || <span className="sbom-pill sbom-pill--warn">no version</span>}</td>
                      <td className="sbom-hide-s sbom-muted">{r.type}</td>
                      <td className="sbom-hide-s sbom-mono">{r.licence || '—'}</td>
                      <td className="sbom-hide-s">{r.supplier || '—'}</td>
                      {vulnOn && (
                        <>
                          <td><VulnChips vulns={r.vulns} checkedAt={r.checked_at} checkable={r.checkable} onOpen={(v) => setAssess(v)} /></td>
                          <td><WorstVerdict vulns={r.vulns} /></td>
                        </>
                      )}
                      <td className="sbom-hide-s"><Support r={r} /></td>
                      <td className="sbom-icon-acts">
                        <button type="button" className="sbom-act sbom-act--icon" title="Open the component page — fields, releases, vulnerabilities, risks"
                          aria-label="Open component" onClick={() => navigate(`/bom/sw-components?component=${encodeURIComponent(r.item_id)}`)}><OpenIcon /></button>
                        <button type="button" className="sbom-act sbom-act--icon" title="Where used — releases that contain this package"
                          aria-label="Where used" onClick={() => setWhereFor(r.item_id)}><WhereUsedIcon /></button>
                        <button type="button" className="sbom-act sbom-act--icon" title={`Copy package URL (purl)\n${r.purl || ''}`}
                          aria-label="Copy purl"
                          onClick={() => {
                            navigator.clipboard?.writeText(r.purl).then(
                              () => showToast('Package URL copied', 'success'),
                              () => showToast(r.purl, 'info'));
                          }}><CopyIcon /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {selected?.imported && (
        <div className="sbom-legend" role="note" aria-label="Legend">
          <span className="sbom-legend-h">Legend</span>
          <span className="sbom-legend-i"><span className="sbom-glyph sbom-glyph--dir">D</span> Direct dependency</span>
          <span className="sbom-legend-i"><span className="sbom-glyph sbom-glyph--ind">I</span> Brought in by another package</span>
          <span className="sbom-legend-note">A package used by several parents appears under each one, like a part in a hardware BOM.</span>
        </div>
      )}

      {exportFor && <SbomExportModal release={exportFor} vulnOn={vulnOn} onClose={() => setExportFor(null)} />}
      {deleteFor && (
        <SbomDeleteModal release={deleteFor} onClose={() => setDeleteFor(null)} onAuth={onAuth}
          onDeleted={(r) => {
            setDeleteFor(null);
            setDeleted(r);
            setKids({});
            setOpen(new Set());
            setSel('');
            load();
          }} />
      )}
      {assess && (
        <AssessDrawer vulnId={assess.id} pkg={assess.package_key} info={info} onAuth={onAuth}
          onClose={() => setAssess(null)} onSaved={refresh} />
      )}
      {whereFor && <SbomWhereUsedModal itemId={whereFor} info={info} onClose={() => setWhereFor('')} onAuth={onAuth} />}
      {compare && (
        <SbomCompareModal releases={releases.filter((r) => r.imported)} initial={cmpPair} onClose={() => setCompare(false)}
          onAuth={onAuth} onLocate={startLocate} />
      )}
    </>
  );
}

/** A release's vulnerability scorecard: open (to assess + affected, not controlled), to assess, KEV,
 *  and the severity bar. "Checked" says how many components OSV could answer for. */
function VulnStats({ v }) {
  const tot = Math.max(1, v.critical + v.high + v.medium + v.low);
  return (
    <>
      <div className="sbom-stats">
        <div className={`sbom-stat${v.open ? ' sbom-stat--bad' : ' sbom-stat--ok'}`}><div className="sbom-stat-n">{v.open}</div><div className="sbom-stat-l">Open</div></div>
        <div className={`sbom-stat${v.to_assess ? ' sbom-stat--warn' : ''}`}><div className="sbom-stat-n">{v.to_assess}</div><div className="sbom-stat-l">To assess</div></div>
        <div className={`sbom-stat${v.kev ? ' sbom-stat--bad' : ''}`}><div className="sbom-stat-n">{v.kev}</div><div className="sbom-stat-l">KEV</div></div>
      </div>
      <div className="sbom-sevbar" title={`${v.critical} critical · ${v.high} high · ${v.medium} medium · ${v.low} low`}>
        <i style={{ width: `${(v.critical / tot) * 100}%`, background: '#7A1230' }} />
        <i style={{ width: `${(v.high / tot) * 100}%`, background: '#B42318' }} />
        <i style={{ width: `${(v.medium / tot) * 100}%`, background: '#C27C00' }} />
        <i style={{ width: `${(v.low / tot) * 100}%`, background: '#A8A1BE' }} />
      </div>
      <div className="sbom-tiny sbom-muted">
        {v.total} known vulnerabilit{v.total === 1 ? 'y' : 'ies'}
        {v.kev > 0 && <> · <span className="sbom-kev">KEV</span> {v.kev} actively exploited</>}
        {v.checked < v.components && <> · {v.components - v.checked} of {v.components} components not checked</>}
      </div>
    </>
  );
}

/** Level of support / end of support — an end date in the past or within a year is flagged. */
export function Support({ r }) {
  const eos = String(r.end_of_support || '').trim();
  const lvl = String(r.support_level || '').trim();
  if (!eos && !lvl) return <span className="sbom-muted">—</span>;
  let tone = 'plain';
  const d = eos ? Date.parse(eos) : NaN;
  if (!Number.isNaN(d)) {
    const days = (d - Date.now()) / 864e5;
    tone = days < 0 ? 'bad' : days < 365 ? 'warn' : 'plain';
  }
  if (/no longer|abandon|unsupported|end of life/i.test(lvl)) tone = 'bad';
  return <span className={`sbom-pill sbom-pill--${tone}`}>{[lvl, eos && `EOS ${eos}`].filter(Boolean).join(' · ')}</span>;
}
